import { useReloadHumioUI } from '../../../../../../shared/hooks';

import { Board, Column, Panel } from '@gamma/ui/layout';
import { ErrorBoundary } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useContext, useEffect, useState } from 'react';
import { isSafari } from 'react-device-detect';
import { humioDashboard } from '../../../../../../shared/constants';
import { GetHumioDashboardsQuery } from '../../../../../../shared/generated/graphql';
import { i18n } from '../../../../../../shared/localization';
import { LogScaleContext } from '../../../../../../shared/providers';
import { hideHumioInnerNav } from '../../../../../../shared/utils';
import {
  HumioLoaderOverlay,
  HumioUnavailable,
} from '../../../../../investigations/components';

export const SensorsMonitoringDashboard = () => {
  const { dashboard } = i18n.pages.sensorMonitoring;

  const {
    iframeKey,
    logScaleDashboardsLoading,
    logScaleDashboardsError,
    logScaleDashboardsData,
  } = useContext(LogScaleContext) as {
    iframeKey: number;
    logScaleDashboardsLoading: boolean;
    logScaleDashboardsError: Error | null;
    logScaleDashboardsData?: GetHumioDashboardsQuery;
  };

  const [dashboardId, setDashboardId] = useState<string | null>('');

  useEffect(() => {
    logScaleDashboardsData?.getHumioDashboards?.searchDomain?.dashboards?.forEach(
      (dashboard) => {
        if (dashboard?.name === humioDashboard.sensor) {
          setDashboardId(dashboard.id || null);
          return;
        }
      },
    );
  }, [logScaleDashboardsData]);

  const { iframeSrc } = useReloadHumioUI({
    iframeType: 'sensorsMonitoring',
    dashboardId: dashboardId ?? '',
  });

  if (
    logScaleDashboardsError ||
    (logScaleDashboardsData &&
      !logScaleDashboardsData.getHumioDashboards?.searchDomain?.dashboards) ||
    (logScaleDashboardsData &&
      logScaleDashboardsData.getHumioDashboards?.searchDomain?.dashboards
        ?.length === 0) ||
    dashboardId === null
  ) {
    return (
      <HumioUnavailable
        unavailable={dashboard.unavailable}
        techSupport={dashboard.techSupport}
      />
    );
  } else if (logScaleDashboardsLoading || !logScaleDashboardsData) {
    // Loading and data can become out of sync so show loading screen
    return (
      <ErrorBoundary page="SensorsMonitoring" styleClass="main">
        <Panel>
          <GraphQLReqStatus
            isBackground={true}
            loading={
              Boolean(logScaleDashboardsLoading) || !logScaleDashboardsData
            }
          />
        </Panel>
      </ErrorBoundary>
    );
  }
  return (
    <ErrorBoundary page="SensorsMonitoring" styleClass="main">
      <Board
        flexGrow={1}
        flexWrap="inherit"
        flexDirection="column"
        data-testid="sensor-stats-humio-embed"
      >
        <Column
          flexGrow={1}
          display="flex"
          flexBasis="auto"
          overflow="hidden"
          flexDirection="column"
        >
          {isSafari && <HumioLoaderOverlay reloadKey={iframeKey} />}
          {iframeSrc && dashboardId && (
            <iframe
              data-testid="sensor-management-dashboard"
              title="ui"
              id="humio-ui"
              key={iframeKey}
              width="100%"
              height="100%"
              style={{
                flexGrow: 1,
                display: 'flex',
                paddingBottom: 0,
              }}
              src={iframeSrc}
              onLoad={() => hideHumioInnerNav()}
            />
          )}
        </Column>
      </Board>
    </ErrorBoundary>
  );
};
